import { Recommendation } from '../../lib/api/intelligence';

interface RecommendationEffectivenessProps {
  recommendations: Recommendation[];
  loading?: boolean;
}

export default function RecommendationEffectiveness({
  recommendations,
  loading,
}: RecommendationEffectivenessProps) {
  const sorted = [...recommendations].sort((a, b) => b.acceptance_rate - a.acceptance_rate);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="border-b border-gray-200 dark:border-white/[0.05] px-6 py-4">
        <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
          Recommendation Effectiveness
        </h3>
      </div>

      <div className="p-6">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
          </div>
        )}

        {!loading && sorted.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
            No recommendation data available.
          </p>
        )}

        {!loading && sorted.length > 0 && (
          <div className="space-y-3">
            {sorted.map((rec, i) => {
              const isTop = i === 0 && rec.acceptance_rate > 0;
              const isBottom = i === sorted.length - 1 && sorted.length > 1;
              return (
                <div
                  key={rec.id}
                  className={`p-3 rounded-lg ${
                    isTop
                      ? 'bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20'
                      : isBottom
                      ? 'bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20'
                      : 'bg-gray-50 dark:bg-white/[0.02]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-white/90 truncate">
                        {rec.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {rec.field_name}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-bold text-gray-800 dark:text-white/90">
                      {Math.round(rec.acceptance_rate * 100)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all ${
                          rec.acceptance_rate >= 0.7
                            ? 'bg-green-500'
                            : rec.acceptance_rate >= 0.4
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.round(rec.acceptance_rate * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                      {rec.times_accepted}/{rec.times_shown}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
